/**
 * Generic smart text formatting that fits as many items as possible within character constraints
 * @param {Array} items - Array of text strings to format
 * @param {number} maxChars - Maximum number of characters allowed
 * @param {Object} options - Configuration options
 * @param {string} options.separator - Separator between items (default: ', ')
 * @param {Function} options.transform - Function to transform each item (default: identity)
 * @returns {string} Formatted text string with appropriate truncation and count
 */
export const formatTextWithEllipsis = (
  items = [], 
  maxChars = 18,
  options = {}
) => {
  const {
    separator = ', ',
    transform = (text) => text,
  } = options;

  if (!items || items.length === 0) {
    return '';
  }
  
  // Transform and filter items
  const transformedItems = items.map(transform).filter(Boolean);
  
  if (transformedItems.length === 0) {
    return '';
  }

  // Single item case - truncate if it exceeds maxChars
  if (transformedItems.length === 1) {
    const singleItem = transformedItems[0];
    if (singleItem.length <= maxChars) {
      return singleItem;
    } else {
      // Truncate single item to fit maxChars with "..."
      return singleItem.substring(0, maxChars - 3) + '...';
    }
  }

  let displayText = '';
  let itemsShown = 0;
  
  for (let i = 0; i < transformedItems.length; i++) {
    const currentItem = transformedItems[i];
    const itemSeparator = i === 0 ? '' : separator;
    const remaining = transformedItems.length - i - 1;
    const needsCounter = remaining > 0;
    
    // Always reserve space for counter if there are remaining items
    const counterText = needsCounter ? ` +${remaining}` : '';
    const reservedCounterSpace = needsCounter ? counterText.length : 0;
    const availableSpaceForItem = maxChars - displayText.length - itemSeparator.length - reservedCounterSpace;
    
    // Check if the full item name fits in available space
    if (currentItem.length <= availableSpaceForItem) {
      // Full item fits
      displayText += itemSeparator + currentItem;
      itemsShown++;
      
      // If this was the last item, we're done (no counter needed)
      if (!needsCounter) {
        break;
      }
      // Continue to next item to try to fit more (don't return immediately)
    } else {
      // Item doesn't fit in available space
      if (itemsShown === 0) {
        // This is the first item and it doesn't fit
        if (needsCounter) {
          // Need to truncate the item to make room for counter
          const truncatedLength = maxChars - counterText.length - 3; // Reserve 3 chars for "..."
          if (truncatedLength > 0) {
            return currentItem.substring(0, truncatedLength) + '...' + counterText;
          } else {
            // Not enough space even for truncated item + counter
            return currentItem; // Let CSS ellipsis handle it
          }
        } else {
          // Single item that doesn't fit, truncate it
          if (currentItem.length <= maxChars) {
            return currentItem;
          } else {
            return currentItem.substring(0, maxChars - 3) + '...';
          }
        }
      } else {
        // We have some items already shown, add counter for remaining
        const remainingCount = transformedItems.length - itemsShown;
        return displayText + ` +${remainingCount}`;
      }
    }
  }
  
  // If we processed all items and some didn't fit, add counter
  const totalItems = transformedItems.length;
  if (itemsShown < totalItems) {
    const remainingCount = totalItems - itemsShown;
    return displayText + ` +${remainingCount}`;
  }
  
  return displayText;
};


